/* eslint-disable */
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { IllumLiveService } from '../../core/services/illum-live.service';
import { OrganizationUtilService } from '../../core/services/organization-util.service';
import { DataContextService } from '../../core/services/data-context.service';
import { ArcAngelSharedService } from '../../core/services/arc-angel-shared.service';
import _ from 'lodash';
import { ArcAngelPapiService } from '../../core/services/arc-angel-papi.service';
import { DeviceService } from '../../core/services/device.service';
import { CoreService } from '../../core/services/core.service';
import { WindowService } from '../../core/services/window.service';
import { Procall } from '../../core/services/procall-factory.service';

@Injectable({
  providedIn: 'root',
})
export class ArcAngelMonitorModeService {
  monitorListener = 'monitor-mode-listener';
  data: any = {
    monitorEvents: [],
    monitorProcalls: [],
    monitorUpcomingCalendar: [],
    monitorOngoingCalendar: [],
    monitorMissedCalendar: [],
    monitorActiveTracking: [],
    numberOfActiveIncidents: 0,
    calls: 0,
    procallcalls: 0,
    calendarcalls: 0,
    activetrackingcalls: 0,
  };
  currentEntitiesAdded: any = {
    incidents: {},
    procalls: {},
    updates: {},
    safezones: {},
    musters: {},
  };
  monitorLiveActiveTrackings:any
  private isRunning = false;
  private firstPollerCalled = false;
  private currentEventsProcallsPollerRemoved = false;
  private pollerSubscription: Subscription | null = null;
  private monitorEventsSubject = new BehaviorSubject<any>([]);
  public monitorEvents$ = this.monitorEventsSubject.asObservable();
  constructor(
    private illumLiveService: IllumLiveService,
    private organizationutilService: OrganizationUtilService,
    private dataContext: DataContextService,
    private arcAngelSharedService: ArcAngelSharedService,
    private arcAngelPapiService: ArcAngelPapiService,
    private deviceService: DeviceService,
    private core: CoreService,
    private windowService: WindowService,
  ) {}

  startMonitorMode(): Observable<any> {
    this.isRunning = true;
    this.firstPollerCalled = true;
    this.currentEventsProcallsPollerRemoved = false;
    this.setupMonitorModeObjects();
    const initialPoll$ = this.currentEventsProcallsPoller().pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      finalize(() => this.kickOffCurrentEventsProcallsPoller())
    );
    initialPoll$.subscribe({
      error: (error) => {
        console.error('Error in startMonitorMode initial poll:', error);
      },
    });
    return initialPoll$;
  }

  stopMonitorMode(): void {
    this.isRunning = false;
    this.removePoller();
    this.currentEventsProcallsPollerRemoved = true;
    this.dataContext.removeMapEntityAddedListener(this.monitorListener);
    this.clearMonitorMode();
    this.core.getViewer().camera.switchToPerspectiveFrustum();
}
  clearMonitorMode(): void {
    for (const incidentId in this.currentEntitiesAdded.incidents) {
      this.dataContext.removeMapEntity(this.currentEntitiesAdded.incidents[incidentId], true);
    }
    for (const procallId in this.currentEntitiesAdded.procalls) {
      this.dataContext.removeMapEntity(this.currentEntitiesAdded.procalls[procallId], true);
    }
    for (const musterId in this.currentEntitiesAdded.musters) {
      this.dataContext.removeMapEntity(this.currentEntitiesAdded.musters[musterId], true);
    }

    this.currentEntitiesAdded = {
      incidents: {},
      procalls: {},
      updates: {},
      safezones: {},
      musters: {},
    };

    this.data.monitorEvents = [];
    this.data.monitorProcalls = [];
    this.data.monitorOngoingCalendar = [];
    this.data.monitorMissedCalendar = [];
    this.data.monitorActiveTracking = [];
  }

  isMonitorModeRunning(): boolean {
    return this.isRunning;
  }

  // --- Poller logic ---
  private kickOffCurrentEventsProcallsPoller(): void {
    this.removePoller();
    this.pollerSubscription = timer(0, 4000)
      .pipe(switchMap(() => this.currentEventsProcallsPoller()))
      .subscribe();
    this.currentEventsProcallsPollerRemoved = false;
  }

  removePoller(): void {
    if (this.pollerSubscription) {
      this.pollerSubscription.unsubscribe();
      this.pollerSubscription = null;
    }
    this.windowService.closeAllMapEntityDetailsWindow();
  }

  private setupMonitorModeObjects(): void {
    _.forEach(this.organizationutilService.data.myFacilityPins, (pin) => {
      this.dataContext.addMapEntity(pin);
    });
  }

  private currentEventsProcallsPoller(): Observable<void> {
    if (this.currentEventsProcallsPollerRemoved || !this.firstPollerCalled) {
      return new Observable<void>((observer) => observer.complete());
    }

    return forkJoin({
      eventData: this.illumLiveService
        .getAllIncidents('active,pending')
        .pipe(catchError(() => of([]))),
      procallData: this.illumLiveService
        .getAllProcalls(false)
        .pipe(catchError(() => of([]))),
      trackingData: this.illumLiveService
        .getCurrentActiveTrackingEvents()
        .pipe(catchError(() => of({}))),
    }).pipe(
      tap((results) => {
        if (!results) return;
        const eventData = results.eventData || [];
        const procallData = results.procallData || [];
        const trackingData = results.trackingData || {};

        for (const incidentId in this.currentEntitiesAdded.incidents) {
          const incident = (
            this.currentEntitiesAdded.incidents as Record<string, any>
          )[incidentId];
          if (!this.dataContext.isMapEntityOnMap(incident.getId())) {
            try {
              this.dataContext.addMapEntity(incident);
            } catch (e) {
              console.error('Error in addMapEntity:', e, incident);
            }
          }
        }
        this.parseCurrentEvents(eventData);
        this.parseActiveTrackingEventsAndProcalls(trackingData, procallData);
      }),
      map(() => void 0), // Return void
      catchError((error) => {
        console.error('Error in currentEventsProcallsPoller:', error);
        return of(void 0); // Continue polling even on error
      })
    );
  }

  // --- Parsing and utility methods ---
  parseCurrentEvents(eventData: any[]): void {
    this.data.numberOfActiveIncidents = 0;
    this.removeResolvedIncidentsFromBuildings(eventData);
    const incidentIdsActivePending: Record<string, boolean> = {};
    if(eventData && eventData.length > 0) {
      this.data.numberOfActiveIncidents = eventData.length;
    for (const incident of eventData) {
      let incidentEntity: any;
      // Check if it's a campus incident or building incident by checking affectedPerimeterIds against campus IDs
      const campuses = this.organizationutilService.data?.myCampus?.campuses || [];
      const campusIds = new Set(campuses.map((c: any) => String(c.campusId)));
      const incidentPerimeterIds = incident.affectedPerimeters?.map((p: any) => String(p.perimeterId)) || [];
      const isCampusIncident = incidentPerimeterIds.some((perimId: string) => campusIds.has(perimId));
      
      /// *** ToDo *** here we are calling the get user status by perimeter further may be we need to change the call
    
      if (isCampusIncident) {
        // For campus incidents, use campus device count
        this.deviceService.getCountCampusDevicesByPerimeterAndStatus(+incident.id).subscribe(() => {
          this.illumLiveService.updatePerimeterSafetyStatus(incident.id, 1);
        });
      } else {
        // For building incidents, use building device count
        this.deviceService.getCountDevicesByPerimeterAndStatus(+incident.id).subscribe(() => {
          this.illumLiveService.updatePerimeterSafetyStatus(incident.id, 1);
        });
      }

      // Show low/med geometry if missing
      if (this.currentEntitiesAdded.incidents) {
        for (const inc of Object.values(this.currentEntitiesAdded.incidents)) {
          const incidentEntity = inc as any;
          // LOW geometries
          if (incidentEntity.lowGeometryMapEntities?.length) {
            for (const geo of incidentEntity.lowGeometryMapEntities) {
              const geoId = geo?.getId?.() ?? geo?.id;
              const isPresent = geoId ? this.dataContext.isCesiumEntityPresent(geoId) : false;

              if (!isPresent) {
                this.dataContext.addMapEntity(geo);
                geo.addOutline?.();
              } else {
                geo.show?.();
              }
            }
          }

          // MEDIUM geometries
          if (incidentEntity.medGeometryMapEntities?.length) {
            for (const geo of incidentEntity.medGeometryMapEntities) {
              const geoId = geo?.getId?.() ?? geo?.id;
              const isPresent = geoId ? this.dataContext.isCesiumEntityPresent(geoId) : false;

              if (!isPresent) {
                this.dataContext.addMapEntity(geo);
                geo.addOutline?.();
              } else {
                geo.show?.();
              }
            }
          }

          // HIGH geometries
          if (incidentEntity.highGeometryMapEntities?.length) {
            for (const geo of incidentEntity.highGeometryMapEntities) {
              const geoId = geo?.getId?.() ?? geo?.id;
              const isPresent = geoId ? this.dataContext.isCesiumEntityPresent(geoId) : false;

              if (!isPresent) {
                this.dataContext.addMapEntity(geo);
                geo.addOutline?.();
              } else {
                geo.show?.();
              }
            }
          }

          // if (
          //   incidentEntity.lowGeometryMapEntity &&
          //   !incidentEntity.lowGeometryMapEntity.addedToMap
          // ) {
          //   this.dataContext.addMapEntity(incidentEntity.lowGeometryMapEntity);
          //   incidentEntity.lowGeometryMapEntity.addOutline();
          // }
          // if (
          //   incidentEntity.medGeometryMapEntity &&
          //   !incidentEntity.medGeometryMapEntity.addedToMap
          // ) {
          //   this.dataContext.addMapEntity(incidentEntity.medGeometryMapEntity);
          //   incidentEntity.medGeometryMapEntity.addOutline();
          // }

        }
      }

      // Incidents
      incidentIdsActivePending[incident.id] = true;
      if (
        !(incident.id in this.currentEntitiesAdded.incidents) &&
        !(incident.id in this.currentEntitiesAdded.musters)
      ) {
        // New incident
        incidentEntity = this.arcAngelSharedService.parseIncident(
          incident,
          incident.id,
          false
        );
        if (!incidentEntity) continue;

        incidentEntity.incidentPerimeter = incident?.incidentPerimeter;
        if (incident.isMuster) {
          this.dataContext.addMapEntity(incidentEntity);
          incidentEntity.show();
          this.currentEntitiesAdded.musters[incident.id] = incidentEntity;
        } else {
          if (incidentEntity.isActive()) {
            //this.data.numberOfActiveIncidents++;
            //this.activeIncidentsSubject.next(this.data.numberOfActiveIncidents);
          }
          this.dataContext.addMapEntity(incidentEntity);
          this.currentEntitiesAdded.incidents[incident.id] = incidentEntity;
          this.data.monitorEvents.push(incidentEntity);
          //this.monitorEventsSubject.next([...this.data.monitorEvents]);
          this.arcAngelPapiService.setBuildingIncident(incidentEntity);
        }
      } else if (incident.isMuster) {
        // nothing for now
      } else if (!incident.isMuster) {
        incidentEntity = this.arcAngelSharedService.updateIncident(
          incident,
          this.currentEntitiesAdded.incidents[incident.id]
        );
        if (incidentEntity.isActive()) {
          //this.data.numberOfActiveIncidents++;
          //this.activeIncidentsSubject.next(this.data.numberOfActiveIncidents);
        }
      }

      if (incident.isMuster) {
        // const geofence = this.organizationutilService.data.myGeofencesByIncidentId[incident.id];
        // if (geofence) {
        //   this.userService.getDevicesForMusterIncident(geofence, currentEntitiesAdded.musters[incident.id]).then((found: any) => {
        //     if (geofence.found !== found) {
        //       geofence.found = found;
        //       return this.userService.getUserProfile(geofence.profileId);
        //     }
        //     return null;
        //   }).then((result: any) => {
        //     if (result) {
        //       const alert: any = {
        //         title: geofence.label + '1'
        //       };
        //       if (geofence.found) {
        //         alert.shortDescription = 'Device Enter; ' + result.getName();
        //         alert.src = 'img/arcangel/geo_enter.png';
        //       } else {
        //         alert.shortDescription = 'Device Exit; ' + result.getName();
        //         alert.src = 'img/arcangel/geo_exit.png';
        //       }
        //       const geofenceAlert = new GeofenceAlert(alert, geofence);
        //       geofenceAlert.setEntity(currentEntitiesAdded.musters[incident.id]);
        //       this.dataFeedNotificationService.addNotificationObject(geofenceAlert);
        //     }
        //   }).catch((error: any) => {
        //   });
        // }
      } else {
        if (
          incident.affectedPerimeters &&
          incident.affectedPerimeters.length > 0
        ) {
          this.currentEntitiesAdded.incidents[
            incident.id
          ].setAffectedPerimeters(
            this.organizationutilService.getAffectedPerimeterEntities(
              incident.affectedPerimeters
            )
          );
        }

        // Incident updates
        const updateIds: Record<string, boolean> = {};
        for (const update of incident.updates) {
          updateIds[update.id] = true;
          if (!(update.id in this.currentEntitiesAdded.updates)) {
            const updateEntity = this.arcAngelSharedService.parseIncidentUpdate(
              update,
              incident,
              false
            );
            if (updateEntity) {
              this.currentEntitiesAdded.updates[update.id] = updateEntity;
              this.currentEntitiesAdded.incidents[incident.id].removeUpdate(
                updateEntity
              );
              this.currentEntitiesAdded.incidents[incident.id].addUpdate(
                updateEntity
              );
              this.addIncidentUpdateToBuildingIncident(updateEntity);
            }
          } else {
            this.arcAngelSharedService.updateIncidentUpdate(
              update,
              this.currentEntitiesAdded.updates[update.id]
            );
          }
        }

        // Safe zones
        const safezoneIds: Record<string, boolean> = {};
        for (const safezone of incident.safezones) {
          const isSzActive = safezone.isActive !== false && safezone.isActive !== 'false' && safezone.active !== false && safezone.active !== 'false';
          if (!isSzActive) {
            if (safezone.id in this.currentEntitiesAdded.safezones) {
              const existingEntity = this.currentEntitiesAdded.safezones[safezone.id];
              delete this.currentEntitiesAdded.safezones[safezone.id];
              if (this.currentEntitiesAdded.incidents[incident.id]?.removeSafeZone) {
                this.currentEntitiesAdded.incidents[incident.id].removeSafeZone(existingEntity);
              }
              this.dataContext.removeMapEntity(existingEntity, true);
            }
            continue;
          }
          safezoneIds[safezone.id] = true;
          if (!(safezone.id in this.currentEntitiesAdded.safezones)) {
            const safeZoneEntity =
              this.arcAngelSharedService.parseIncidentSafezone(
                safezone,
                incident,
                true
              );
            if (safeZoneEntity) {
              this.currentEntitiesAdded.safezones[safezone.id] = safeZoneEntity;
              this.currentEntitiesAdded.incidents[incident.id].addSafeZone(
                safeZoneEntity
              );
            }
          } else {
            this.arcAngelSharedService.updateIncidentSafezone(
              safezone,
              this.currentEntitiesAdded.safezones[safezone.id]
            );
          }
        }

        // Remove deleted safezones and updates
        for (const safezone of [
          ...this.currentEntitiesAdded.incidents[incident.id].getSafeZones(),
        ]) {
          if (!(safezone.getArcAngelId() in safezoneIds)) {
            this.currentEntitiesAdded.incidents[incident.id].removeSafeZone(
              safezone
            );
            this.dataContext.removeMapEntity(safezone, true);
          }
        }
        // for (const update of [
        //   ...this.currentEntitiesAdded.incidents[incident.id].getUpdates(),
        // ]) {
        //   if (!(update.getArcAngelId() in updateIds)) {
        //     this.currentEntitiesAdded.incidents[incident.id].removeUpdate(
        //       update
        //     );
        //     this.dataContext.removeMapEntity(update, true);
        //   }
        // }
      }

      // Set incident polygon height to above ground
      const incidentHeight = incidentEntity.incidentPerimeter
        ? incidentEntity.incidentPerimeter.height
        : 1;
      if (incidentEntity.cesiumEntity && incidentEntity.cesiumEntity.polygon) {
        incidentEntity.cesiumEntity.polygon.height = incidentHeight;
      }
      if (incidentEntity.outline.getAltitude() !== incidentHeight) {
        incidentEntity.outline.setAltitude(incidentHeight);
      }
      /// ** Here we are updating the perimeter safety status
      this.illumLiveService.updatePerimeterSafetyStatus(incident.id, 1);
    }
  }
    // Remove resolved events
    for (const [incidentId, incident] of Object.entries(
      this.currentEntitiesAdded.incidents
    )) {
      if (!(incidentId in incidentIdsActivePending)) {
        _.pull(this.data.monitorEvents, incident);
        // this.userService.removeDevicesForIncident(incident);
        this.dataContext.removeMapEntity(incident as any, true);
        const typedIncident = incident as any & {
          buildingMapEntity?: any;
        };
        if (typedIncident.buildingMapEntity) {
          typedIncident.buildingMapEntity.resetPerimeterSafetyStatus();
        }
        delete this.currentEntitiesAdded.incidents[incidentId];
      }
    }

    this.data.calls++;
    this.monitorEventsSubject.next(this.data.calls)
  }
  removeResolvedIncidentsFromBuildings(eventData: any[]): void {
    const eventIds = eventData.map((event) => event.id);

    for (const building of this.organizationutilService.data.myBuildings) {
      for (const incident of building.incidents) {
        if (
          incident &&
          incident.eventId &&
          !eventIds.includes(incident.eventId)
        ) {
          building.removeIncident(incident.eventId);
        }
      }
    }
  }
  clearMonitorUpdates(): void {
    // Remove update entities from map
    for (const updateId in this.currentEntitiesAdded.updates) {
      const updateEntity = this.currentEntitiesAdded.updates[updateId];
      this.dataContext.removeMapEntity(updateEntity, true);
    }
    // Clear updates dictionary
    this.currentEntitiesAdded.updates = {};
  }

  async startMonitorIncidentMode(incidentId: string, scope?: any): Promise<void> {
    if (!incidentId) {
      return Promise.reject(new Error('Invalid incidentId provided for monitor mode'));
    }

    this.windowService.closeAllMapEntityDetailsWindow();

    try {
      if (!this.isMonitorModeRunning()) {
        await firstValueFrom(this.startMonitorMode());
      }

      await this.focusIncidentInMonitorMode(incidentId, scope);
    } catch (error) {
      console.error('Error starting monitor incident mode.', error);
      throw error;
    }
  }

  private focusIncidentInMonitorMode(incidentId: string, scope?: any): Promise<void> {
    const maxAttempts = 5;
    const retryDelayMs = 400;

    return new Promise((resolve, reject) => {
      const attemptFocus = (attempt: number) => {
        const incidentEntity = this.getMonitorIncidentEntity(incidentId);

        if (incidentEntity) {
          incidentEntity.show?.();
          
          // For building incidents, open the building camera panel (similar to live mode)
          // Pass the entity itself as scope so useEntityCamera receives the correct entity
          if (typeof incidentEntity.isBuildingIncident === 'function' && incidentEntity.isBuildingIncident()) {
            if (typeof incidentEntity.viewDetailsWindow === 'function') {
              // Pass entity with sessionOptionsService attached for Monitor Mode compatibility
              const entityWithScope = Object.assign({}, incidentEntity, { sessionOptionsService: scope?.sessionOptionsService });
              incidentEntity.viewDetailsWindow(entityWithScope);
            }
          } else {
            // For non-building incidents, navigate to location
            if (typeof incidentEntity.goToEntity === 'function') {
              incidentEntity.goToEntity();
            }
          }
          resolve();
          return;
        }

        if (attempt >= maxAttempts) {
          reject(new Error(`Unable to locate incident ${incidentId} in monitor mode`));
          return;
        }

        setTimeout(() => attemptFocus(attempt + 1), retryDelayMs);
      };

      attemptFocus(0);
    });
  }

  private getMonitorIncidentEntity(incidentId: string): any {
    return (
      this.currentEntitiesAdded?.incidents?.[incidentId] ||
      this.dataContext.getMapEntity(incidentId)
    );
  }

  parseActiveTrackingEventsAndProcalls(activeTrackingData: any[], procallData: any[]) {
    _.forEach(this.data.monitorActiveTracking, (value) => {
      if (!activeTrackingData.some((event: any) => event.id === value.id)) {
        this.dataContext.removeMapEntity(this.currentEntitiesAdded.activeTrackings?.[value.id]);
        delete this.currentEntitiesAdded.activeTrackings?.[value.id];
      }
    });

    this.data.monitorActiveTracking = [];
    _.forEach(activeTrackingData, (value) => {
      value.panicButtonClicked = false;
      if (this.isInDistress(value, procallData)) {
        value.panicButtonClicked = true;
        this.dataContext.removeMapEntity(this.currentEntitiesAdded.activeTrackings?.[value.id]);
        delete this.currentEntitiesAdded.activeTrackings?.[value.id];
      }

      value = this.arcAngelSharedService.parseActiveTrackingEvent(value , false);

      if (!this.isInDistress(value, procallData)) {
        this.data.monitorActiveTracking.push(value);

        this.dataContext.addMapEntity(value);
      }
    });
    this.data.activetrackingcalls++;

    const procallKeys: Record<string, boolean> = {};
    _.forEach(procallData, (item: any) => {
      procallKeys[item.id] = true;

      if (item.type === 'procall') {
        const procallId = item.id;
        const profileId = item.profileId;
        const status = item.status;
        const dateCreated = item.createdAt;
        const dateUpdated = item.updatedAt;
        const parentId = item.parentId;

        if (status === 'open' || status === 'pending') {
          const newProcall = new Procall(procallId, profileId, status, dateCreated, dateUpdated, parentId);

          if (!(procallId in this.currentEntitiesAdded.procalls)) {
            this.data.monitorProcalls.push(newProcall);
            this.currentEntitiesAdded.procalls[procallId] = newProcall;

            this.deviceService.getUserProfile(profileId).then((profile: any) => {
              if (profile && typeof profile.getName === 'function') {
                newProcall.setName(profile.getName());
              } else if (profile) {
                newProcall.setName(profile);
              }
            }).catch((error: any) => {
              console.error('Error getting user profile.', error);
            });
          }

          this.dataContext.addMapEntity(newProcall);

          const devices = this.deviceService.getProcallDevices();
          _.forEach(devices, (device: any) => {
            device.cesiumEntity.show = true;
            const perimeter = this.organizationutilService.data.myBeacons?.[device.getClosestBeacon()];

            if (device.getAssociatedPerimeter() && (!perimeter || device.getAssociatedPerimeter().getArcAngelId() !== perimeter.getArcAngelId())) {
              device.getAssociatedPerimeter().removeTrackingDevice(device);
            }

            device.setAssociatedPerimeter(perimeter);

            if (perimeter) {
              perimeter.addTrackingDevice(device);
            }
          });
        } else if (procallId in this.currentEntitiesAdded.procalls) {
          this.dataContext.removeMapEntity(this.currentEntitiesAdded.procalls[procallId]);
          _.pull(this.data.monitorProcalls, this.currentEntitiesAdded.procalls[procallId]);
          delete this.currentEntitiesAdded.procalls[procallId];
        }
      }
    });

    _.forEach(this.currentEntitiesAdded.procalls, (value, key) => {
      if (!(key in procallKeys)) {
        this.dataContext.removeMapEntity(value);
        _.pull(this.data.monitorProcalls, value);
        delete this.currentEntitiesAdded.procalls[key];
      }
    });

    this.data.procallcalls++;
  }

  private isInDistress(value: any, procallData: any[]): boolean {
    return procallData.some((procall: any) => procall.profileId === value.profileId);
  }


  addIncidentUpdateToBuildingIncident(updateEntity: any): void {
    for (const building of this.organizationutilService.data.myBuildings) {
      for (const incident of building.incidents) {
        if (incident.eventId === updateEntity.eventId) {
          // Remove if exists
          const index = incident.updates.findIndex(
            (update: any) => update.data.id === updateEntity.data.id
          );
          if (index !== -1) {
            incident.updates.splice(index, 1);
          }
          incident.updates.push(updateEntity);
        }
      }
    }
  }
  getOrgIdForLiveIncident(id: string): string | null {
    const events = this.data.monitorEvents.filter(
      (event: any) => event.eventId === id
    );
    if (events[0]?.obj?.organizationIds) {
      return events[0]?.obj?.organizationIds;
    }else{
      return events.length > 0 ? events[0].orgid : null;
    }
  }
  getMonitorEvents(): any[] {
    return [...this.data.monitorEvents];
  }
  clearIncidents(): void {
    this.data.numberOfActiveIncidents = 0;
    this.data.monitorEvents = [];
    this.currentEntitiesAdded.incidents = [];
    //this.monitorEventsSubject.next([]);
  }
  // user.service.ts
getActiveTrackingDevice(trackingEvent: any): any {
  return this.monitorLiveActiveTrackings?.[trackingEvent.id];
}
}
